import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PatientProvider } from './context/PatientContext';
import { PatientProfileProvider } from './context/PatientProfileContext';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import AboutUsPage from './pages/AboutUsPage';
import LetsWorkTogetherPage from './pages/LetsWorkTogetherPage';
import RegistrationPage from './pages/RegistrationPage';
import OtpVerificationPage from './pages/OtpVerificationPage';
import LoginPage from './pages/LoginPage';
import PatientDashboard from './pages/PatientDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminDashboardNew from './pages/AdminDashboardNew';
import PatientDashboardNew from './pages/PatientDashboardNew';
import PharmacyDashboardNew from './pages/PharmacyDashboardNew';
import PharmacyPatientsPage from './pages/PharmacyPatientsPage';
import PharmacyInsurancePage from './pages/PharmacyInsurancePage';
import PharmacyHIVTestingPage from './pages/PharmacyHIVTestingPage';
import PharmacyTelehealthPage from './pages/PharmacyTelehealthPage';
import PharmacyPrescriptionsPage from './pages/PharmacyPrescriptionsPage';
import PharmacyRefillAdherencePage from './pages/PharmacyRefillAdherencePage';
import CommunityDashboardNew from './pages/CommunityDashboardNew';
import CampusDashboardNew from './pages/CampusDashboardNew';
import CampusSupport from './pages/CampusSupport';
import QuestionnaireFlow from './pages/QuestionnaireFlow';
import IntakeBlockA from './pages/IntakeBlockA';
import IntakeBlockB from './pages/IntakeBlockB';
import MDIVoucher from './pages/MDIVoucher';
import AdminLoginPage from './pages/AdminLoginPage';
import PatientDetailPage from './pages/PatientDetailPage';
import DocsViewer from './components/DocsViewer';
import FrontendGuide from './pages/FrontendGuide';
import ComingSoon from './pages/ComingSoon';
import CommunityReferrals from './pages/CommunityReferrals';
import CommunityPatientsPage from './pages/CommunityPatientsPage';
import AdminPatientsPage from './pages/AdminPatientsPage';
import CommunityHIVTestingPage from './pages/CommunityHIVTestingPage';
import CommunityRefillAdherencePage from './pages/CommunityRefillAdherencePage';
import HIVTestingPatientDetailPage from './pages/HIVTestingPatientDetailPage';
import InsurancePatientDetailPage from './pages/InsurancePatientDetailPage';
import TelehealthPatientDetailPage from './pages/TelehealthPatientDetailPage';
import PrescriptionPatientDetailPage from './pages/PrescriptionPatientDetailPage';

import ConsentToTelehealthPage from './pages/ConsentToTelehealthPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PharmacyPrescriptionPatientDetailPage from './pages/PharmacyPrescriptionPatientDetailPage';

import CarePipelinePage from './pages/CarePipelinePage';
import PartnerManagementPage from './pages/PartnerManagementPage';
import AdminMarketsPage from './pages/AdminMarketsPage';
import AddUserPage from './pages/AddUserPage';
import PatientSettings from './pages/PatientSettings';
import PatientSupport from './pages/PatientSupport';
import PharmacySettings from './pages/PharmacySettings';
import { PharmacyProvider } from './context/PharmacyContext';
import PharmacyDetailPage from './pages/PharmacyDetailPage';
import CommunityPartnerDetailPage from './pages/CommunityPartnerDetailPage';
import AmbassadorDetailPage from './pages/AmbassadorDetailPage';
import CommunitySettings from './pages/CommunitySettings';

import './styles/App.css';
import MedicationManagementPage from './pages/MedicationManagementPage';
import CampusProfile from './pages/CampusProfile';
import PharmacyMedicationManagementPage from './pages/PharmacyMedicationManagementPage';
import CommunityInsurancePage from './pages/CommunityInsurancePage';
import CommunityHIVTestingPatientDetailPage from './pages/CommunityHIVTestingPatientDetailPage';

// Set to true to show the maintenance/redirect page to all visitors.
// Set to false to restore normal site access.
const MAINTENANCE_MODE = true;

const App: React.FC = () => {
  if (MAINTENANCE_MODE) {
    window.location.replace('/maintenance.html');
    return null;
  }

  return (
    <Router>
      <AuthProvider>
        <PatientProvider>
          <PatientProfileProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutUsPage />} />
              <Route path="/work-together" element={<LetsWorkTogetherPage />} />
              <Route path="/register" element={<RegistrationPage />} />
              <Route path="/verify-otp" element={<OtpVerificationPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/docs" element={<DocsViewer />} />
              <Route path="/frontend-guide" element={<FrontendGuide />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/consent-to-telehealth" element={<ConsentToTelehealthPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

              {/* Protected Routes */}
              <Route
                path="/patient/intake-a"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <IntakeBlockA />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/intake-b"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <IntakeBlockB />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/mdi-voucher"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <MDIVoucher />
                  </ProtectedRoute>
                }
              />
              <Route path="/questionnaire"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <QuestionnaireFlow />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <PatientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/dashboard-new"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <PatientDashboardNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/my-care"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <ComingSoon title="My Care" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/medications"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <ComingSoon title="Medications" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/appointments"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <ComingSoon title="Appointments" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/profile"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <ComingSoon title="Profile" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/support"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <PatientSupport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/settings"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <PatientSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/hiv-testing"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <HIVTestingPatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/insurance"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <InsurancePatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/telehealth"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <TelehealthPatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/medication-management"
                element={
                  <ProtectedRoute allowedRoles={['PATIENT']}>
                    <PrescriptionPatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/dashboard-new"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login">
                    <AdminDashboardNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/patient/:patientId"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login">
                    <PatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pharmacy/:pharmacyId"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login">
                    <PharmacyDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/community-partner/:partnerId"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login">
                    <CommunityPartnerDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ambassador/:ambassadorId"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login">
                    <AmbassadorDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin Supplemental Routes */}
              <Route
                path="/admin/patients"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login">
                    <AdminPatientsPage />
                  </ProtectedRoute>
                }
              />

              {/* Care Pipeline */}
              <Route path="/admin/care/hiv-testing" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><CarePipelinePage type="HIV_TESTING" title="Care Pipeline: HIV Testing" description="View and manage patient HIV test results and status." /></ProtectedRoute>} />
              <Route path="/admin/care/hiv-testing/:patientId" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><HIVTestingPatientDetailPage /></ProtectedRoute>} />
              <Route path="/admin/care/insurance" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><CarePipelinePage type="INSURANCE" title="Care Pipeline: Insurance" description="Manage patient insurance verification and coverage status." /></ProtectedRoute>} />
              <Route path="/admin/care/insurance/:patientId" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><InsurancePatientDetailPage /></ProtectedRoute>} />
              <Route path="/admin/care/telehealth" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><CarePipelinePage type="TELEHEALTH" title="Care Pipeline: Telehealth" description="Monitor and update patient telehealth appointment status." /></ProtectedRoute>} />
              <Route path="/admin/care/telehealth/:patientId" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><TelehealthPatientDetailPage /></ProtectedRoute>} />
              <Route path="/admin/care/prescriptions" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><CarePipelinePage type="PRESCRIPTIONS" title="Care Pipeline: Prescriptions" description="Track prescription issuance and fulfillment progress." /></ProtectedRoute>} />
              <Route path="/admin/care/medication-management/:patientId" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><PrescriptionPatientDetailPage /></ProtectedRoute>} />
              <Route path="/admin/care/medication-management" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><MedicationManagementPage /></ProtectedRoute>} />

              {/* Partners */}
              <Route path="/admin/partners/pharmacy" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><PartnerManagementPage role="PHARMACY_PARTNER" title="Pharmacy Partners" description="Manage access and settings for pharmacy partner accounts." /></ProtectedRoute>} />
              <Route path="/admin/partners/community" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><PartnerManagementPage role="COMMUNITY_PARTNER" title="Community Partners" description="Monitor and manage community organization partner accounts." /></ProtectedRoute>} />
              <Route path="/admin/partners/ambassadors" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><PartnerManagementPage role="CAMPUS_AMBASSADOR" title="Campus Ambassadors" description="Manage campus ambassador accounts and coverage areas." /></ProtectedRoute>} />

              {/* Ops */}
              <Route path="/admin/markets" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><AdminMarketsPage /></ProtectedRoute>} />
              <Route path="/admin/users/add" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><AddUserPage /></ProtectedRoute>} />
              <Route path="/admin/users/edit/:userId" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><AddUserPage /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']} redirectPath="/admin/login"><ComingSoon title="Analytics / Reports" /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['SUPER_ADMIN']} redirectPath="/admin/login"><AddUserPage from="setting" /></ProtectedRoute>} />
              <Route
                path="/pharmacy/*"
                element={
                  <ProtectedRoute allowedRoles={['PHARMACY_PARTNER']} redirectPath="/admin/login">
                    <PharmacyProvider>
                      <Routes>
                        <Route path="dashboard-new" element={<PharmacyDashboardNew />} />
                        <Route path="patients" element={<PharmacyPatientsPage />} />
                        <Route path="insurance" element={<PharmacyInsurancePage />} />
                        <Route path="community-partners" element={<ComingSoon title="Community Partners" />} />
                        <Route path="hiv-testing" element={<PharmacyHIVTestingPage />} />
                        <Route path="telehealth" element={<PharmacyTelehealthPage />} />
                        <Route path="prescriptions" element={<PharmacyPrescriptionsPage />} />
                        <Route path="refill-adherence" element={<PharmacyRefillAdherencePage />} />
                        <Route path="performance" element={<ComingSoon title="Performance" />} />
                        <Route path="resources" element={<ComingSoon title="Resources" />} />
                        <Route path="medication-management" element={<PharmacyMedicationManagementPage />} />
                        <Route path="medication-management/:patientId" element={<PharmacyPrescriptionPatientDetailPage />} />
                        <Route path="settings" element={<PharmacySettings />} />
                        <Route path="patients/:patientId" element={<PatientDetailPage />} />
                        <Route path="support" element={<PatientSupport />} />
                        <Route path="insurance/:patientId" element={<InsurancePatientDetailPage />} />
                        <Route path="telehealth/:patientId" element={<TelehealthPatientDetailPage />} />
                        <Route path="hiv-testing/:patientId" element={<HIVTestingPatientDetailPage />} />

                      </Routes>
                    </PharmacyProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community/dashboard-new"
                element={
                  <ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login">
                    <CommunityDashboardNew />
                  </ProtectedRoute>
                }
              />
              <Route path="/community/referrals" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><CommunityReferrals /></ProtectedRoute>} />
              <Route path="/community/patients" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><CommunityPatientsPage /></ProtectedRoute>} />
              <Route
                path="/community/patient/:patientId"
                element={
                  <ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login">
                    <PatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/community/hiv-testing" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><CommunityHIVTestingPage /></ProtectedRoute>} />
              <Route path="/community/hiv-testing/:patientId" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><HIVTestingPatientDetailPage /></ProtectedRoute>} />

              <Route path="/community/insurance" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><CommunityInsurancePage /></ProtectedRoute>} />
              <Route path="/community/insurance/:patientId" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><InsurancePatientDetailPage /></ProtectedRoute>} />

              <Route path="/community/refill-adherence" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><CommunityRefillAdherencePage /></ProtectedRoute>} />
              <Route path="/community/settings" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><CommunitySettings /></ProtectedRoute>} />
              <Route path="/community/support" element={<ProtectedRoute allowedRoles={['COMMUNITY_PARTNER']} redirectPath="/admin/login"><PatientSupport /></ProtectedRoute>} />
              <Route
                path="/campus/dashboard-new"
                element={
                  <ProtectedRoute allowedRoles={['CAMPUS_AMBASSADOR']} redirectPath="/admin/login">
                    <CampusDashboardNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campus/support"
                element={
                  <ProtectedRoute allowedRoles={['CAMPUS_AMBASSADOR']} redirectPath="/admin/login">
                    <CampusSupport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campus/settings"
                element={
                  <ProtectedRoute allowedRoles={['CAMPUS_AMBASSADOR']} redirectPath="/admin/login">
                    <CampusProfile from="profile" />
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PatientProfileProvider>
        </PatientProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
